import * as Yup from 'yup';
import {
    conditionallyRequired,
    displayIf,
    requiredCheck,
    requiredIf,
    requiredMessage,
    fileSize,
    fileType,
    validRating,
    validFile,
} from '../../../utils/extensions/yup-extensions';
import { RatingsMap } from '../../../constants/chips/ratings.constants';
import { useFileConstraints } from '../../../utils/features/use-file-constraints';
import { useCommentConstraints } from '../../../utils/features/use-comment-constraints';

/**
 * Creates a subcomponent form schema with feature-aware constraints
 * @param {Object} fileConstraints - File upload constraints from useFileConstraints()
 * @param {Object} commentConstraints - Comment constraints from useCommentConstraints()
 * @returns {Yup.ObjectSchema} The form validation schema
 */
export const makeSubcomponentFormSchema = (fileConstraints, commentConstraints) => {
    const { maxBytes, allowedMimes, allowedExts } = fileConstraints;
    const { maxLength } = commentConstraints;

    return Yup.object().shape({
        uploadEvidence: Yup.mixed()
            .label('Upload Evidence')
            .required(requiredMessage)
            .nullable()
            .test(fileSize(maxBytes))
            .test(fileType(allowedMimes, allowedExts))
            .test(validFile())
            .meta({ fileConstraints }),

        rating: Yup.array()
            .label('Rating')
            .required(requiredMessage)
            .nullable()
            .max(1)
            .test(validRating(RatingsMap.default)),

        comment: Yup.string()
            .label('Comment')
            .nullable()
            .max(maxLength)
            .when('rating', requiredIf([RatingsMap.red, RatingsMap.amber, RatingsMap.yellow]))
            .test(
                conditionallyRequired('rating', [
                    RatingsMap.yellow,
                    RatingsMap.amber,
                    RatingsMap.red,
                ])
            )
            .meta({
                displayIf: displayIf('rating', [
                    RatingsMap.yellow,
                    RatingsMap.amber,
                    RatingsMap.red,
                ]),
                requiredIf: requiredCheck('rating', [
                    RatingsMap.yellow,
                    RatingsMap.amber,
                    RatingsMap.red,
                ]),
            }),
    });
};

export const makeEditSubcomponentFormSchema = (fileConstraints, commentConstraints) => {
    const { maxBytes, allowedMimes, allowedExts } = fileConstraints;
    const { maxLength } = commentConstraints;

    return Yup.object().shape({
        uploadEvidence: Yup.mixed()
            .label('Upload Evidence')
            .nullable() // Not required in edit mode because of how file upload input works
            .test(fileSize(maxBytes))
            .test(fileType(allowedMimes, allowedExts))
            .meta({ fileConstraints }),

        rating: Yup.array()
            .label('Rating')
            .required(requiredMessage)
            .nullable()
            .max(1)
            .test(validRating(RatingsMap.default)),

        comment: Yup.string()
            .label('Comment')
            .nullable()
            .max(maxLength)
            .when('rating', requiredIf([RatingsMap.red, RatingsMap.amber, RatingsMap.yellow]))
            .test(
                conditionallyRequired('rating', [
                    RatingsMap.yellow,
                    RatingsMap.amber,
                    RatingsMap.red,
                ])
            )
            .meta({
                displayIf: displayIf('rating', [
                    RatingsMap.yellow,
                    RatingsMap.amber,
                    RatingsMap.red,
                ]),
                requiredIf: requiredCheck('rating', [
                    RatingsMap.yellow,
                    RatingsMap.amber,
                    RatingsMap.red,
                ]),
            }),
    });
};

/**
 * Hook to create a feature-aware form schema
 * This should be used in components to get a schema that respects current feature toggles
 * @returns {Yup.ObjectSchema} The form validation schema
 */
export const useSubcomponentFormSchema = (mode = 'capture') => {
    const fileConstraints = useFileConstraints();
    const commentConstraints = useCommentConstraints();

    return mode == 'capture'
        ? makeSubcomponentFormSchema(fileConstraints, commentConstraints)
        : makeEditSubcomponentFormSchema(fileConstraints, commentConstraints);
};

export const makeViewEvidenceFormSchema = (commentConstraints) => {
    const { maxLength } = commentConstraints;

    return Yup.object().shape({
        uploadEvidence: Yup.mixed().label('Uploaded Evidence').nullable(),

        rating: Yup.array()
            .label('Rating')
            .required(requiredMessage)
            .nullable()
            .max(1)
            .test(validRating(RatingsMap.default)),

        comment: Yup.string()
            .label('Comment')
            .nullable()
            .max(maxLength)
            .when('rating', requiredIf([RatingsMap.red, RatingsMap.amber, RatingsMap.yellow]))
            .test(
                conditionallyRequired('rating', [
                    RatingsMap.yellow,
                    RatingsMap.amber,
                    RatingsMap.red,
                ])
            )
            .meta({
                displayIf: displayIf('rating', [
                    RatingsMap.yellow,
                    RatingsMap.amber,
                    RatingsMap.red,
                ]),
                requiredIf: requiredCheck('rating', [
                    RatingsMap.yellow,
                    RatingsMap.amber,
                    RatingsMap.red,
                ]),
            }),
    });
};

export const useViewEvidenceFormSchema = () => {
    const commentConstraints = useCommentConstraints();
    return makeViewEvidenceFormSchema(commentConstraints);
};
